import React, { useState, useMemo, useEffect, useRef } from 'react';
import { filedBpBaseline, baselineMissingCopy } from '@/logic/baseline';
import { useMeasureReadings } from '../state/data/patient';
import { measureWindowYmd, OVERVIEW_SERIES_DAYS } from '../logic/measureWindow';
import useNow from '../hooks/useNow';
import {
  pairBpReadings, computeBpSummary, classifyBp, BP_CLASS_LABELS, BP_CLASS_TIER,
  BP_CLASS_DETAIL, BP_DISPLAY_ORDER,
} from '../logic/bpReadings';
import BpReadingsListModal from './BpReadingsListModal';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend, ArcElement);

/* ── Constants ────────────────────────────────────────────────── */

// Hex serves the chart canvas (chart.js can't read CSS vars) + legacy DOM accents
// in this card — TODO tokens for the DOM call sites. Labels come from the clinical logic.
// Hypotension breaks the red ramp on purpose — it is Tier 1 but a *low* reading,
// and folding it into the high-BP gradient is how it read as green before.
const CLS = {
  hypotension: { color: '#2563EB', bg: '#DBEAFE', label: BP_CLASS_LABELS.hypotension },
  controlled:  { color: '#22C55E', bg: '#DCFCE7', label: BP_CLASS_LABELS.controlled },
  elevated:    { color: '#65A30D', bg: '#ECFCCB', label: BP_CLASS_LABELS.elevated },
  stage2:      { color: '#EA580C', bg: '#FFF7ED', label: BP_CLASS_LABELS.stage2 },
  crisis:      { color: '#DC2626', bg: '#FEE2E2', label: BP_CLASS_LABELS.crisis },
  emergency:   { color: '#7F1D1D', bg: '#FECACA', label: BP_CLASS_LABELS.emergency },
};

function fmtDate(epoch) {
  return new Date(epoch * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* ── Alert pill ───────────────────────────────────────────────── */

// The CLS hex above is a CANVAS palette — chart.js can't read CSS vars, so the doughnut and
// the legend dots need literals. This pill is DOM, so it takes witems' text/background pairs
// instead: the saturated slice colours run 3.3-4.2:1 as text on their own tint, below the
// 4.5:1 AA floor. Same reason the readings-list badges use these pairs.
const ALERT_PALETTE = {
  hypotension: { fg: 'var(--wi-text-primary-info)', bg: 'var(--wi-background-info)', edge: 'var(--wi-status-info)' },
  stage2:      { fg: 'var(--wi-text-primary-moderate)', bg: 'var(--wi-background-moderate)', edge: 'var(--wi-status-moderate)' },
  crisis:      { fg: 'var(--wi-text-primary-bad)', bg: 'var(--wi-background-bad)', edge: 'var(--wi-status-bad)' },
  emergency:   { fg: 'var(--wi-text-primary-bad)', bg: 'var(--wi-background-bad)', edge: 'var(--wi-status-bad)' },
};

function AlertPill({ alertReadings }) {
  if (!alertReadings || alertReadings.length === 0) return null;
  // alertReadings is worst-first, so [0] IS the worst and its class sets the tone.
  const worst = alertReadings[0];
  const worstCls = classifyBp(worst.systolic, worst.diastolic);
  const c = ALERT_PALETTE[worstCls];

  // Insertion order follows alertReadings, so the counts read worst-first too.
  const perClass = new Map();
  for (const r of alertReadings) {
    const cls = classifyBp(r.systolic, r.diastolic);
    perClass.set(cls, (perClass.get(cls) ?? 0) + 1);
  }
  const label = [...perClass].map(([k, n]) => `${n} ${BP_CLASS_LABELS[k].toLowerCase()}`).join(' · ');

  return (
    <span
      title={`Worst: ${worst.systolic}/${worst.diastolic} on ${fmtDate(worst.epoch)} — Tier ${BP_CLASS_TIER[worstCls]}\n${BP_CLASS_DETAIL[worstCls]}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
        background: c.bg, color: c.fg, border: `1px solid ${c.edge}`, cursor: 'default',
      }}
    >
      ⚠ {label}
    </span>
  );
}

/* ── Doughnut chart for category distribution ─────────────────── */

function CategoryDoughnut({ buckets, count }) {
  const active = BP_DISPLAY_ORDER.filter(k => buckets[k] > 0);

  const data = {
    labels: active.map(k => CLS[k].label),
    datasets: [{
      data: active.map(k => buckets[k]),
      backgroundColor: active.map(k => CLS[k].color),
      borderWidth: 2,
      borderColor: '#fff',
      hoverOffset: 4,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.9)',
        bodyFont: { size: 12, family: 'Aeonik, sans-serif', weight: '500' },
        padding: 8,
        cornerRadius: 6,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        callbacks: {
          label: (item) => ` ${item.label}: ${item.raw} (${Math.round((item.raw / count) * 100)}%)`,
        },
      },
    },
  };

  return (
    <div style={{ position: 'relative', width: 90, height: 90, flexShrink: 0 }}>
      <Doughnut data={data} options={options} />
      {/* Center text */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        textAlign: 'center', pointerEvents: 'none',
      }}>
        <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1, display: 'block' }}>{count}</span>
        <span style={{ fontSize: 9, color: 'var(--wi-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>readings</span>
      </div>
    </div>
  );
}

function CategoryLegend({ buckets }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {BP_DISPLAY_ORDER.map(k => {
        if (buckets[k] === 0) return null;
        return (
          <div key={k} title={BP_CLASS_DETAIL[k]} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: CLS[k].color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'var(--wi-text-primary)', fontWeight: 500 }}>{buckets[k]}</span>
            <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)' }}>{CLS[k].label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Baseline display ─────────────────────────────────────────── */

function BaselineInfo({ cmsSubmissions, userid }) {
  // The baseline is what CMS received (bp_avg_sent), NOT measure_state.baseline_value — that one
  // rolls on every reading and disagreed with the filing by 18 mmHg on a live patient whose
  // Care Update was already faxed. No filing => no number. logic/baseline.ts owns the rule.
  const filed = filedBpBaseline(cmsSubmissions, userid);

  if (!filed) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--wi-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Baseline</span>
        <span style={{ fontSize: 12, color: 'var(--wi-text-placeholder)', fontStyle: 'italic' }}>{baselineMissingCopy(cmsSubmissions, userid)}</span>
      </div>
    );
  }
  const baselineSys = filed.sys;
  const baselineDia = filed.dia;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--wi-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Baseline</span>
      <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {baselineSys != null ? Math.round(baselineSys) : '—'}/{baselineDia != null ? Math.round(baselineDia) : '—'}
      </span>
      <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)' }}>mmHg</span>
    </div>
  );
}

/* ── Trend section ────────────────────────────────────────────── */

// "Worsening" here is the same verdict the trend alert and the patient-home badge use
// (classifyBpTrend / WF9 trendDir): >= 8 mmHg on the 7-day mean vs the prior 14 days.
function TrendInfo({ summary }) {
  if (summary.trend === 'insufficient') {
    return <span style={{ fontSize: 12, color: 'var(--wi-text-placeholder)', fontStyle: 'italic' }}>Not enough data for trend</span>;
  }

  const icon = summary.trend === 'improving' ? '↓' : summary.trend === 'worsening' ? '↑' : '→';
  const color = summary.trend === 'improving' ? '#22C55E' : summary.trend === 'worsening' ? '#DC2626' : 'var(--wi-text-secondary)';
  const label = summary.trend === 'improving' ? 'Improving' : summary.trend === 'worsening' ? 'Worsening' : 'Stable';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color }}>{icon}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color }}>
          {summary.trendDelta > 0 ? '+' : ''}{summary.trendDelta} mmHg
        </span>
      </div>
      <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)' }}>
        Last 7d avg <b>{summary.recentAvg}</b> vs prior 14d <b>{summary.priorAvg}</b>
      </span>
    </div>
  );
}

/* ── Aggregate readings by day ─────────────────────────────────── */

// Chart days are VIEWER-LOCAL; the trend means above are UTC (WF3/WF9 parity). Don't
// harmonize them — a UTC x-axis slides every evening reading off the viewer's own calendar.
function aggregateByDay(readings) {
  const dayMap = new Map();
  for (const r of readings) {
    const dayKey = new Date(r.epoch * 1000).toLocaleDateString('en-CA');
    if (!dayMap.has(dayKey)) dayMap.set(dayKey, []);
    dayMap.get(dayKey).push(r);
  }

  return Array.from(dayMap.entries()).map(([day, rds]) => {
    const sysVals = rds.map(r => r.systolic);
    const diaVals = rds.map(r => r.diastolic);
    const avgSys = Math.round(sysVals.reduce((a, b) => a + b, 0) / sysVals.length);
    const avgDia = Math.round(diaVals.reduce((a, b) => a + b, 0) / diaVals.length);
    return {
      day, epoch: rds[0].epoch, count: rds.length,
      avgSys, avgDia,
      minSys: Math.min(...sysVals), maxSys: Math.max(...sysVals),
      minDia: Math.min(...diaVals), maxDia: Math.max(...diaVals),
    };
  }).sort((a, b) => a.epoch - b.epoch);
}


/* ── Worst-of-day color (uses the highest reading, not avg) ──── */

function worstDayColor(d) {
  if (d.avgSys == null) return 'transparent';
  const sys = d.maxSys ?? d.avgSys;
  const dia = d.maxDia ?? d.avgDia;
  return CLS[classifyBp(sys, dia)].color;
}

/* ── Chart.js plugin: draw pills (rounded capsules) for min→max ─ */

const pillPlugin = {
  id: 'bpPills',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const gridData = chart.config._bpGrid;
    if (!gridData) return;

    const yScale = chart.scales.y;
    const sysMeta = chart.getDatasetMeta(0);

    const PILL_W = 8; // pill width in px
    const R = PILL_W / 2; // border radius (fully rounded)

    ctx.save();

    for (let i = 0; i < sysMeta.data.length; i++) {
      const d = gridData[i];
      if (!d || d.avgSys == null) continue;

      const x = sysMeta.data[i].x;
      const color = worstDayColor(d);
      const hasRange = d.count > 1;

      // — Systolic pill/dot —
      const sysTop = yScale.getPixelForValue(hasRange ? d.maxSys : d.avgSys);
      const sysBot = yScale.getPixelForValue(hasRange ? d.minSys : d.avgSys);
      const sysH = Math.max(sysBot - sysTop, PILL_W); // min height = circle
      const sysY = sysTop - (sysH - (sysBot - sysTop)) / 2;

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(x - R, sysY, PILL_W, sysH, R);
      ctx.fill();
      // White border
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // — Diastolic pill/dot —
      const diaTop = yScale.getPixelForValue(hasRange ? d.maxDia : d.avgDia);
      const diaBot = yScale.getPixelForValue(hasRange ? d.minDia : d.avgDia);
      const diaH = Math.max(diaBot - diaTop, PILL_W);
      const diaY = diaTop - (diaH - (diaBot - diaTop)) / 2;

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(x - R, diaY, PILL_W, diaH, R);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    ctx.restore();
  },
};

/* ── Line chart ───────────────────────────────────────────────── */

function BpChart({ readings, rangeDays, targetSystolic, onDayClick }) {
  const targetSys = targetSystolic ?? 130; // care-plan systolic target; 130 = clinical default
  const rawDays = useMemo(() => aggregateByDay(readings), [readings]);

  // Build full calendar grid matching the selected range
  const grid = useMemo(() => {
    const now = new Date();
    now.setHours(12, 0, 0, 0);
    const dayMap = new Map();
    rawDays.forEach(d => dayMap.set(d.day, d));

    const result = [];
    for (let i = rangeDays - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const key = date.toLocaleDateString('en-CA');
      const existing = dayMap.get(key);
      result.push(existing || { day: key, epoch: Math.floor(date.getTime() / 1000), count: 0, avgSys: null, avgDia: null, minSys: null, maxSys: null, minDia: null, maxDia: null });
    }
    return result;
  }, [rawDays, rangeDays]);

  const labels = grid.map(d => fmtDate(d.epoch));

  const data = {
    labels,
    datasets: [
      // Systolic (hidden dots — pills drawn by plugin)
      {
        label: 'Systolic',
        data: grid.map(d => d.avgSys),
        showLine: false,
        pointRadius: 0,
        pointHoverRadius: 7,
        pointBackgroundColor: 'transparent',
        pointBorderColor: 'transparent',
      },
      // Diastolic (hidden dots — pills drawn by plugin)
      {
        label: 'Diastolic',
        data: grid.map(d => d.avgDia),
        showLine: false,
        pointRadius: 0,
        pointHoverRadius: 7,
        pointBackgroundColor: 'transparent',
        pointBorderColor: 'transparent',
      },
      // Systolic target line (from the care plan; 130 default)
      {
        label: `Target ${targetSys}`,
        data: grid.map(() => targetSys),
        borderColor: 'rgba(34,197,94,0.35)',
        borderWidth: 1.5,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
      },
    ],
  };

  const withData = grid.filter(d => d.avgSys != null);
  const allDia = withData.map(d => d.minDia ?? d.avgDia);
  const allSys = withData.map(d => d.maxSys ?? d.avgSys);
  const minVal = Math.min(...allDia) - 10;
  const maxVal = Math.max(...allSys, targetSys) + 10;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Click, hover and the tooltip footer read chart.config._bpGrid (the plugin's
    // source) so the "clickable" promise and the click gate can't diverge.
    onClick: (evt, elements, chart) => {
      if (!elements.length) return;
      const g = chart.config._bpGrid || [];
      const d = g[elements[0].index];
      if (!d || d.count === 0) return;
      onDayClick?.(d.day);
    },
    onHover: (evt, elements, chart) => {
      const g = chart.config._bpGrid || [];
      const d = elements.length ? g[elements[0].index] : null;
      chart.canvas.style.cursor = d && d.count > 0 ? 'pointer' : 'default';
    },
    plugins: {
      legend: {
        display: true, position: 'bottom', align: 'start',
        labels: {
          usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 16,
          font: { size: 11, family: 'Aeonik, sans-serif' }, color: '#6B7280',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.9)',
        titleFont: { size: 12, family: 'Aeonik, sans-serif', weight: '500' },
        bodyFont: { size: 12, family: 'Aeonik, sans-serif', weight: '500' },
        footerFont: { size: 11, family: 'Aeonik, sans-serif', weight: '400' },
        footerColor: 'rgba(255,255,255,0.65)',
        padding: { top: 8, bottom: 8, left: 12, right: 12 },
        cornerRadius: 8, displayColors: false,
        callbacks: {
          title: (items) => items[0]?.label || '',
          label: (item) => {
            if (item.datasetIndex !== 0) return null;
            const d = grid[item.dataIndex];
            if (!d || d.avgSys == null) return null;
            const worstCls = classifyBp(d.maxSys ?? d.avgSys, d.maxDia ?? d.avgDia);
            if (d.count > 1) {
              return [
                `SYS ${d.minSys}–${d.maxSys} (avg ${d.avgSys})`,
                `DIA ${d.minDia}–${d.maxDia} (avg ${d.avgDia})`,
                `${d.count} readings · ${CLS[worstCls].label}`,
              ];
            }
            return `${d.avgSys}/${d.avgDia} mmHg · ${CLS[worstCls].label}`;
          },
          footer: (items) => {
            const it = items[0];
            const d = it ? (it.chart.config._bpGrid || [])[it.dataIndex] : null;
            return d && d.count > 0 ? '›  Click to view readings' : '';
          },
        },
        filter: (item) => item.datasetIndex === 0,
      },
    },
    scales: {
      x: {
        offset: true, // inset first/last points off the axis border (else a recent reading clips at the edge)
        ticks: { font: { size: 10 }, color: '#9CA3AF', maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
        grid: { color: 'rgba(0,0,0,0.03)' },
        border: { display: false },
      },
      y: {
        min: Math.floor(minVal / 10) * 10,
        max: Math.ceil(maxVal / 10) * 10,
        ticks: { font: { size: 10 }, color: '#9CA3AF', stepSize: 20 },
        grid: { color: 'rgba(0,0,0,0.04)' },
        border: { display: false },
      },
    },
    interaction: { intersect: false, mode: 'index' },
  };

  // Attach grid data for the plugin to read
  const chartRef = useRef(null);
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.config._bpGrid = grid;
      chartRef.current.update('none');
    }
  }, [grid]);

  return (
    <div style={{ position: 'relative', height: 220 }}>
      <Line ref={chartRef} data={data} options={options} plugins={[pillPlugin]} />
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────────────── */

export default function BpReadingsCard({ userid, compact, targetSystolic, cmsSubmissions = null }) {
  const [rangeDays, setRangeDays] = useState(OVERVIEW_SERIES_DAYS);
  // null | { focusRange: [startEpoch, endEpoch] | null } — conditional mount only
  // (the modal derives its initial page in a useState initializer).
  const [listModal, setListModal] = useState(null);

  const nowMs = useNow();
  const { startdateymd, enddateymd } = useMemo(() => measureWindowYmd(rangeDays, nowMs), [rangeDays, nowMs]);

  // BP (9/10) and HR (11) fetch separately: scalews rejects limit > 250
  // ("limit too high"), so one combined call would truncate a 90d window —
  // per-series calls keep the full 250-row page each. HR is optional: its
  // failure or lag never blocks the card, rows just show "—" until it lands.
  const query = useMeasureReadings(userid, '9,10', { startdateymd, enddateymd });
  const hrQuery = useMeasureReadings(userid, '11', { startdateymd, enddateymd });
  const loading = query.isLoading;
  const error = query.error ? (query.error.message || 'Failed to load') : null;
  const rawReadings = useMemo(() => {
    if (!query.data) return null;
    return [...query.data.readings, ...(hrQuery.data?.readings ?? [])];
  }, [query.data, hrQuery.data]);
  const truncated = (query.data?.total ?? 0) > (query.data?.readings?.length ?? 0);

  const paired = useMemo(() => rawReadings ? pairBpReadings(rawReadings) : [], [rawReadings]);
  const summary = useMemo(() => computeBpSummary(paired), [paired]);

  // Chart day keys are viewer-local ('en-CA'); the click becomes an epoch range
  // (midnight → next midnight, DST-safe) so the modal highlights by epoch. The
  // modal PRINTS times in the patient's tz — a clicked day can label ±1 day there.
  const openListForDay = (dayKey) => {
    const dayStart = new Date(`${dayKey}T00:00:00`);
    const nextMidnight = new Date(dayStart);
    nextMidnight.setDate(nextMidnight.getDate() + 1);
    setListModal({
      focusRange: [Math.floor(dayStart.getTime() / 1000), Math.floor(nextMidnight.getTime() / 1000)],
    });
  };

  if (loading) return (
    <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '20px', textAlign: 'center' }}>
      <span className="detail1 text-secondary">Loading BP readings…</span>
    </div>
  );
  if (error) return (
    <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '20px', textAlign: 'center' }}>
      <span className="detail1 text-secondary">Unable to load readings </span>
      <button onClick={() => query.refetch()} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--wi-action-primary)', fontFamily: 'var(--wi-font-family)', fontWeight: 500 }}>Retry</button>
    </div>
  );
  if (!summary) return (
    <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '20px', textAlign: 'center' }}>
      <span className="detail1 text-secondary">No BP readings in the last {rangeDays} days</span>
    </div>
  );

  /* ── Compact ── */
  if (compact) {
    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{summary.avgSystolic}/{summary.avgDiastolic}</span>
          <span style={{ fontSize: 11, color: 'var(--wi-text-secondary)' }}>avg · {summary.count} readings ({rangeDays}d)</span>
        </div>
        <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', gap: 1 }}>
          {['controlled', 'elevated', 'stage2', 'crisis'].map(k => {
            const pct = (summary.buckets[k] / summary.count) * 100;
            if (pct === 0) return null;
            return <div key={k} style={{ width: `${pct}%`, background: CLS[k].color, minWidth: 3 }} />;
          })}
        </div>
      </div>
    );
  }

  /* ── Full (PatientDetail) ── */
  return (
    <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, border: '1px solid var(--wi-divider)', overflow: 'hidden', marginTop: 24 }}>

      {/* Section header with alert pill + range toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="eyebrow text-secondary">BP Readings</span>
          <AlertPill alertReadings={summary.alertReadings} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => setListModal({ focusRange: null })}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
              fontSize: 12, fontWeight: 500, color: 'var(--wi-action-primary)',
              fontFamily: 'var(--wi-font-family)',
            }}
          >
            View all ›
          </button>
          <div style={{ display: 'flex', gap: 2, background: 'var(--wi-background-primary)', borderRadius: 6, padding: 2 }}>
          {[30, 90].map(d => (
            <button key={d} onClick={() => setRangeDays(d)} style={{
              fontSize: 11, fontWeight: rangeDays === d ? 600 : 400,
              padding: '4px 12px', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: rangeDays === d ? 'var(--wi-background-secondary)' : 'transparent',
              color: rangeDays === d ? 'var(--wi-text-primary)' : 'var(--wi-text-secondary)',
              fontFamily: 'var(--wi-font-family)',
              boxShadow: rangeDays === d ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}>{d}d</button>
          ))}
          </div>
        </div>
      </div>

      {/* ── Top section: 3-column summary ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 0, borderBottom: '1px solid var(--wi-divider)' }}>

        {/* Left: Avg BP + Baseline */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--wi-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>
              Average BP
            </span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                {summary.avgSystolic}/{summary.avgDiastolic}
              </span>
              <span style={{ fontSize: 12, color: 'var(--wi-text-secondary)' }}>mmHg</span>
            </div>
          </div>
          <BaselineInfo cmsSubmissions={cmsSubmissions} userid={userid} />
        </div>

        {/* Center: Category doughnut + legend */}
        <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16, borderLeft: '1px solid var(--wi-divider)', borderRight: '1px solid var(--wi-divider)' }}>
          <CategoryDoughnut buckets={summary.buckets} count={summary.count} />
          <CategoryLegend buckets={summary.buckets} />
        </div>

        {/* Right: Trend */}
        <div style={{ padding: '16px 20px' }}>
          <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--wi-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 6 }}>
            14-Day Trend
          </span>
          <TrendInfo summary={summary} />
        </div>
      </div>

      {/* ── Chart ── */}
      {paired.length >= 2 && (
        <div style={{ padding: '8px 16px 4px' }}>
          <BpChart readings={paired} rangeDays={rangeDays} targetSystolic={targetSystolic} onDayClick={openListForDay} />
        </div>
      )}

      {listModal && (
        <BpReadingsListModal
          userid={userid}
          readings={paired}
          rangeDays={rangeDays}
          truncated={truncated}
          focusRange={listModal.focusRange}
          onClose={() => setListModal(null)}
        />
      )}

    </div>
  );
}
